import { useTranslation } from 'react-i18next';
import { Chip } from '@/components/ui/Chip';
import { cn } from '@/components/ui/cn';
import type { Question } from '../types';
import { GlossText } from './GlossText';

export interface ChipSelectProps {
  question: Question;
  value: string[];
  onChange: (next: string[]) => void;
  /** Maximum selections for multi mode. Ignored in single mode. */
  max?: number;
  className?: string;
}

/**
 * Universal selector for single/multi-select questions.
 *
 * Layout switches by content:
 *   - Plain chip row (existing): when no option carries `help_key`.
 *   - Vertical card list: when any option has `help_key`. Each card shows
 *     the chip and a short help paragraph below it; help renders glossary
 *     markers via GlossText. This is the CV.0 §4.4 standard.
 *
 * Click semantics are identical across both layouts.
 *
 * Single: click replaces value with [optionId].
 * Multi: click toggles option; adding past `max` is a no-op (Chip is disabled).
 */
export function ChipSelect({ question, value, onChange, max, className }: ChipSelectProps) {
  const { t } = useTranslation();
  const options = question.options ?? [];
  const isSingle = question.type === 'single';
  const role: 'radio' | 'checkbox' = isSingle ? 'radio' : 'checkbox';
  const effectiveMax = max ?? question.multi_max;
  const limitReached =
    !isSingle && typeof effectiveMax === 'number' && value.length >= effectiveMax;
  const hasHelp = options.some((opt) => Boolean(opt.help_key));

  const toggle = (optionId: string) => {
    if (isSingle) {
      onChange([optionId]);
      return;
    }
    if (value.includes(optionId)) {
      onChange(value.filter((id) => id !== optionId));
      return;
    }
    if (limitReached) return;
    onChange([...value, optionId]);
  };

  if (hasHelp) {
    return (
      <div
        role={isSingle ? 'radiogroup' : 'group'}
        aria-label={t(question.label_key)}
        className={cn('flex flex-col gap-3', className)}
      >
        {options.map((opt) => {
          const selected = value.includes(opt.id);
          const disabled = !isSingle && !selected && limitReached;
          return (
            <div key={opt.id} className="flex flex-col gap-1.5">
              <Chip
                pressed={selected}
                disabled={disabled}
                onClick={() => toggle(opt.id)}
                role={role}
                className="w-full justify-start text-left sm:w-auto"
              >
                {t(opt.label_key)}
              </Chip>
              {opt.help_key && (
                <GlossText
                  i18nKey={opt.help_key}
                  className="block max-w-xl pl-1 text-[13px] leading-relaxed text-ink-500"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role={isSingle ? 'radiogroup' : 'group'}
      aria-label={t(question.label_key)}
      className={cn('flex flex-wrap gap-2', className)}
    >
      {options.map((opt) => {
        const selected = value.includes(opt.id);
        const disabled = !isSingle && !selected && limitReached;
        return (
          <Chip
            key={opt.id}
            pressed={selected}
            disabled={disabled}
            onClick={() => toggle(opt.id)}
            role={role}
          >
            {t(opt.label_key)}
          </Chip>
        );
      })}
    </div>
  );
}
